import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { PartnerStatus, PartnerType } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePodeEditarParceiro } from "@/lib/use-pode-editar-parceiro";

export type FormularioDoCadastro = {
  type: PartnerType;
  name: string;
  tradeName: string;
  email: string;
  emailDeCobranca: string;
  phone: string;
  document: string;
  croNumber: string;
  croState: string;
  specialty: string;
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  neighborhood: string;
  city: string;
  state: string;
  notes: string;
  status: PartnerStatus;
};

export const CADASTRO_VAZIO: FormularioDoCadastro = {
  type: "DENTIST",
  name: "",
  tradeName: "",
  email: "",
  emailDeCobranca: "",
  phone: "",
  document: "",
  croNumber: "",
  croState: "",
  specialty: "",
  zipCode: "",
  address: "",
  addressNumber: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  notes: "",
  status: "ACTIVE",
};

const doBanco = (d: Record<string, any>): FormularioDoCadastro =>
  Object.fromEntries(Object.keys(CADASTRO_VAZIO).map((k) => [k, d[k] ?? (CADASTRO_VAZIO as any)[k]])) as FormularioDoCadastro;

const ou = (v: string) => v.trim() || undefined;

/**
 * O formulário do cadastro, comum ao diálogo de criar e à aba Dados do perfil. Cadastro conectado
 * tem nome, documento, contato, CRO e endereço espelhados do perfil dele (`origemDoDado`): ficam
 * travados aqui e a API também recusa — só e-mail, especialidade, observações e o comercial são
 * do laboratório.
 */
export function useCadastro({ id, tipoInicial, onSalvo }: { id?: string; tipoInicial?: PartnerType; onSalvo?: (id: string) => void }) {
  const utils = trpc.useUtils();
  const podeEditar = usePodeEditarParceiro();
  const detail = trpc.partner.getById.useQuery({ id: id ?? "" }, { enabled: !!id });
  const [values, setValues] = useState<FormularioDoCadastro>({ ...CADASTRO_VAZIO, type: tipoInicial ?? "DENTIST" });
  useEffect(() => {
    if (id && detail.data) setValues(doBanco(detail.data as Record<string, any>));
  }, [id, detail.data]);

  const invalidar = () => {
    utils.partner.list.invalidate();
    utils.partner.countByType.invalidate();
    utils.partner.getById.invalidate();
  };
  const create = trpc.partner.create.useMutation({
    onSuccess: (criado: { id: string }) => {
      toast.success("Cadastro criado.");
      invalidar();
      onSalvo?.(criado.id);
    },
    onError: (err) => toast.error(err.message || "Erro ao criar."),
  });
  const update = trpc.partner.update.useMutation({
    onSuccess: () => {
      toast.success("Cadastro atualizado.");
      invalidar();
      if (id) onSalvo?.(id);
    },
    onError: (err) => toast.error(err.message || "Erro ao atualizar."),
  });

  const vindoDoPerfil = !!id && (detail.data as { origemDoDado?: string } | undefined)?.origemDoDado === "perfil";
  const travado = vindoDoPerfil || !podeEditar;

  const salvar = () => {
    if (!values.name.trim()) return toast.error("Nome é obrigatório.");
    const v = values;
    const doLaboratorio = { type: v.type, tradeName: ou(v.tradeName), email: ou(v.email), emailDeCobranca: v.emailDeCobranca.trim(), specialty: ou(v.specialty), notes: ou(v.notes), status: v.status };
    const payload = vindoDoPerfil
      ? doLaboratorio
      : {
          ...doLaboratorio,
          name: v.name.trim(),
          phone: ou(v.phone),
          document: ou(v.document),
          croNumber: ou(v.croNumber),
          croState: (v.croState as any) || undefined,
          zipCode: ou(v.zipCode),
          address: ou(v.address),
          addressNumber: ou(v.addressNumber),
          addressComplement: ou(v.addressComplement),
          neighborhood: ou(v.neighborhood),
          city: ou(v.city),
          state: (v.state as any) || undefined,
        };
    if (id) update.mutate({ id, ...payload } as any);
    else create.mutate(payload as any);
  };

  return { detail, values, setValues, podeEditar, vindoDoPerfil, travado, salvar, salvando: create.isPending || update.isPending };
}
